import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, defer, Subject } from 'rxjs';
import { shareReplay, startWith, takeUntil } from 'rxjs/operators';
import { AssetClassLandAreaComponentModel, AssetClassLandAreaModel } from 'src/app/core/asset_class';
import { ParcelIdentificationModel } from 'src/app/core/comparable/_models/building.model';
import { AllCoordinateReferenceSystemsRequested, CoordinateReferenceSystem, selectAllCoordinateReferenceSystems } from 'src/app/core/linked-tables';
import { AppState } from 'src/app/core/reducers';
import { LandAreaListComponent } from '../land-parcel/land-area/land-area-list/land-area-list.component';
import { LandParcelMapComponent } from '../land-parcel/land-parcel-map/land-parcel-map.component';

export interface ParcelIdentificationData {
  add_info_on_land: boolean,
  land_parcel_name: string,
  coordinate_reference_system_id: number,
  points: {lat: number, lng: number}[],
  landAreaComponentListSubject: BehaviorSubject<AssetClassLandAreaComponentModel[]>,
  landAreaSubject: BehaviorSubject<AssetClassLandAreaModel>,
  boundary_type_id: number
}

type Point = {
  lat: number;
  lng: number;
}

type DataItem = {
  point: Point;
  length: number;
}

@Component({
  selector: 'kt-parcel-identifaction-section',
  templateUrl: './parcel-identifaction-section.component.html',
  styleUrls: ['./parcel-identifaction-section.component.scss']
})
export class ParcelIdentifactionSectionComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(LandParcelMapComponent, {static: false})
  landParcelMapComponent: LandParcelMapComponent;
  @ViewChild(LandAreaListComponent, {static: false})
  landAreaListComponent: LandAreaListComponent;
  @Input() readonly: boolean;
  @Input() centerLat: number;
  @Input() centerLng: number;
  @Input() data: ParcelIdentificationData;
  @Input() landAreaLowLevelTitle: string = null;
  @Output() checked: EventEmitter<boolean> = new EventEmitter();
  formGroup: UntypedFormGroup;

  coordinateReferenceSystems: CoordinateReferenceSystem[] = [];

  @Input() staticUnitLengthName: string = 'M';
  @Input() staticUnit: number = 2;
  @Input() staticUnitName: string = 'sqm';
  public dataSource$ = new BehaviorSubject<DataItem[]>([])
  public area$: BehaviorSubject<number> = new BehaviorSubject(null);
  imperial_length = 3.28084;
  private _drawingManager: google.maps.drawing.DrawingManager;
  private _shape: google.maps.Polygon;
  private _drawingManagerInitialized$ = new BehaviorSubject(false)
  private _points: Point[] = [];
  private _map: google.maps.Map;

  private _onDestroy$: Subject<void> = new Subject();
  errorFields = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private store$: Store<AppState>
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.data && !changes.data.firstChange) {
      this._populateForm()
      this._points = changes.data.currentValue.points
      this._createPolygon(this._points);
    }
  }

  private _populateForm() {
    if (this.formGroup) {
      this.formGroup.patchValue({
        add_info_on_land: this.data.add_info_on_land,
        land_parcel_name: this.data.land_parcel_name,
        coordinate_reference_system_id: this.data.coordinate_reference_system_id
      });
      this.formGroup.updateValueAndValidity();
    } else {
      this.formGroup = this.formBuilder.group({
        add_info_on_land: [this.data.add_info_on_land],
        land_parcel_name: [this.data.land_parcel_name],
        coordinate_reference_system_id: [this.data.coordinate_reference_system_id],
        boundary_type_id: [this.data.boundary_type_id]
      })
    }
  }

  ngOnInit(): void {
    this.store$.dispatch(new AllCoordinateReferenceSystemsRequested());
    this.store$.pipe(
        select(selectAllCoordinateReferenceSystems),
        takeUntil(this._onDestroy$)
    ).subscribe(res => {
        this.coordinateReferenceSystems = [];
        if (res) {
            this.coordinateReferenceSystems = res;
        }
    });
    this._populateForm();
    this._points = this.data.points
    this._createPolygon(this._points);

    this.formGroup.controls.add_info_on_land.valueChanges.pipe(
      startWith(this.formGroup.controls.add_info_on_land.value),
      takeUntil(this._onDestroy$)
    ).subscribe(val => this.checked.emit(val));

    combineLatest([
      defer(() => this.formGroup.controls.boundary_type_id.valueChanges.pipe(
        startWith(this.formGroup.controls.boundary_type_id.value)
      )).pipe(shareReplay(1)),
      this._drawingManagerInitialized$
    ]).pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(([value, initialized]) => {
      if (!initialized) return
      if (value !== 2) {
        this._disableDrawing()
        if (this._shape) {
          this._shape.setMap(null)
        }
        this._points = []
        this.dataSource$.next([])
        this.area$.next(null)
      } else {
        this._enableDrawing()
      }
    })
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public validate(): boolean {
    let errorFields = [];
    let hasError = false;
    if (this.formGroup.invalid) {
      Object.keys(this.formGroup.controls).forEach(cName => {
        if (this.formGroup.controls[cName].invalid) {
          errorFields = [...errorFields, cName];
        }
        this.formGroup.controls[cName].markAsTouched();
      })
      this.errorFields = errorFields;
      hasError = true;
    }
    return hasError;
  }

  public getData(): ParcelIdentificationModel {
    this.landAreaListComponent && this.landAreaListComponent.updateValue();

    const model = new ParcelIdentificationModel();
    const controls = this.formGroup.controls;
    model.add_info_on_land = controls.add_info_on_land.value;
    model.land_parcel_name = controls.land_parcel_name.value;
    model.coordinate_reference_system_id = controls.coordinate_reference_system_id.value;
    model.points = this._points;
    if (this.landAreaListComponent) {
      model.landArea = this.landAreaListComponent.getData();
    }
    model.landAreaComponents = this.data.landAreaComponentListSubject.value;
    model.boundary_type_id = controls.boundary_type_id.value;
    return model;
  }

  onAddPoint() {
    this._points.push({lat: null, lng: null});
    this._createPolygon(this._points);
  }
  onFocusOut(event: FocusEvent, type: 'lat'|'lng', item: Point) {
    const el = event.target as HTMLInputElement;
    const val = Number(el.value);
    const index = this._points.findIndex(p => p.lat == item.lat && p.lng == item.lng);
    if (index == -1) {
      return;
    }
    let newItem = {...this._points[index]};
    switch (type) {
      case 'lat':
        newItem.lat = val;
        break;
      case 'lng':
        newItem.lng = val;
        break;
    }
    this._points.splice(index, 1, newItem);
    this._createPolygon(this._points);
  }
  onMapReady(map: google.maps.Map) {
    map.controls[google.maps.ControlPosition.TOP_CENTER].push(document.getElementById('DeleteShape'));
    this._initializeDrawingManager(map);
    this._map = map;

    if (this._points.length > 0) {
      this._createPolygon(this._points);
    }
  } 
  onDeleteShape() {}
  private _initializeDrawingManager(map: google.maps.Map) {
    const options: google.maps.drawing.DrawingManagerOptions = {
      drawingMode: google.maps.drawing.OverlayType.POLYGON,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [
          google.maps.drawing.OverlayType.POLYGON
        ]
      },
      polygonOptions: {
        draggable: false,
        editable: true,
        clickable: false
      }
    }

    const drawingManager = new google.maps.drawing.DrawingManager(options);
    drawingManager.setMap(map);
    this._drawingManager = drawingManager
    this._drawingManagerInitialized$.next(true)

    google.maps.event.addListener(drawingManager, 'overlaycomplete', (event) => {
      if (event.type === google.maps.drawing.OverlayType.POLYGON) {
        const points = this._getPointsFromPath(event.overlay.getPath());
        this._createPolygon(points);
        event.overlay.setMap(null);
      }
      if (event.type !== google.maps.drawing.OverlayType.MARKER) {
        this._disableDrawing();
      }
    })
  }
  private _getPointsFromPath(path: any): Point[] {
    const points = [];
    const len = path.getLength();
    for (let i = 0; i < len; i++) {
      points.push({ ...path.getAt(i).toJSON() });
    }
    return points;
  }
  private _createPolygon(points: Point[]) {
    if (this._shape) {
      this._shape.setMap(null);
    }
    const filtered = points.filter(p => p.lat != null && p.lng != null);
    this._shape = new google.maps.Polygon({
      paths: filtered,
      editable: true
    });
    // this._shape.addListener('rightclick', (e: google.maps.MouseEvent) => {
    //   this._mapRightClick(e);
    // })
    this._shape.setMap(this._map);

    const self = this;
    const paths = this._shape.getPaths();
    for (let p = 0; p < paths.getLength(); p++) {
      google.maps.event.addListener(paths.getAt(p), 'set_at', () => {
        self._updatePaths(this._shape.getPath());
      });
      google.maps.event.addListener(paths.getAt(p), 'insert_at', () => {
        self._updatePaths(this._shape.getPath());
      });
      google.maps.event.addListener(paths.getAt(p), 'remove_at', () => {
        self._updatePaths(this._shape.getPath());
      })
    }

    this._updatePoints(points);
  }
  private _updatePaths(path: any) {
    const points = this._getPointsFromPath(path);
    this._updatePoints(points);
  }
  private _updatePoints(points: Point[]) {
    this._points = points;
    const tmp: DataItem[] = this._points.map(p => ({
      point: p,
      length: 0
    }));
    for (let i = 1; i < this._points.length; i++) {
      if (i == this._points.length - 1) {
        tmp[0].length = this._calcLength(this._points[0], this._points[this._points.length - 1]);
        tmp[i].length = this._calcLength(this._points[i], this._points[i - 1]);
      } else {
        tmp[i].length = this._calcLength(this._points[i], this._points[i - 1]);
      }
    }
    this.dataSource$.next(tmp);
    if (this._points.filter(p => p.lat == null || p.lng == null).length > 0) {
      this.area$.next(null);
    } else {
      this.area$.next(google.maps.geometry.spherical.computeArea(this._points.map(p => (new google.maps.LatLng(p.lat, p.lng)))));
    }
  }
  private _disableDrawing() {
    this._drawingManager.setDrawingMode(null);
    this._drawingManager.setOptions({
      drawingControl: false
    })
  }
  private _enableDrawing() {
    this._drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
    this._drawingManager.setOptions({
      drawingControl: true
    })
  }
  private _calcLength(coord1, coord2): number {
    const pathP2P = [];
    const latlng1 = new google.maps.LatLng(coord1);
    const latlng2 = new google.maps.LatLng(coord2);
    pathP2P.push(latlng1);
    pathP2P.push(latlng2);

    return google.maps.geometry.spherical.computeLength(pathP2P);
  }
}
